import {
    AirClass,
    AirParams,
    Amount,
    AvailabilityType,
    Currency,
    GatewayParam,
    Inclusion,
    Occupancy,
    PaxFlights,
    PaxPrice,
    PriceDetail,
    PriceDetailAir,
    PriceDetailCruise,
    PriceErrorResponseV2,
    PriceRequestV2,
    PriceResponseV2,
    RequestAirFare,
    SuitePayment,
} from "@ssc-booking/api-contracts"
import { CruiseCode, DateOnly } from "@ssc-booking/functional-support"
import * as O from "fp-ts/Option"
import { Option } from "fp-ts/Option"
import * as F from "fp-ts/function"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import { FareCode } from "@silversea-ssc/web-fares-fp"
import { InclusionCode } from "@silversea-ssc/web-inclusions"

export const depositA = (percentage: number, totalAmount: Amount, dateAsString: string) => {
    const amount = Amount.multiplyBy(percentage / 100)(totalAmount)
    return SuitePayment.build({
        type: "deposit",
        percentage,
        totalAmount,
        amount,
        balanceAmount: Amount.subtract(totalAmount, amount),
        finalDueDate: DateOnly.build(new Date(dateAsString)),
    })
}

export const deposit = (percentage: number, totalAmountNumber: number, currency: Currency, dateAsString: string) =>
    depositA(percentage, Amount.of(totalAmountNumber, currency), dateAsString)

export const cruiseDetail = (price: number, currency: Currency): PriceDetailCruise =>
    PriceDetail.as.Cruise({ price: Amount.of(price, currency) })

const flightDetail = (
    price: Amount,
    from: string,
    to: string,
    airClass: AirClass,
    isPromotional: boolean,
): PriceDetailAir => PriceDetail.as.Air({ price, from, to, airClass, isPromotional })

export const flightsDetail = (
    price: number,
    currency: Currency,
    from: string,
    outboundTo: string,
    returnFrom: string,
    airClass: AirClass,
    isPromotional: boolean,
): Option<PaxFlights> =>
    O.some({
        outbound: flightDetail(Amount.of(price, currency), from, outboundTo, airClass, isPromotional),
        return: flightDetail(Amount.of(price, currency), returnFrom, from, airClass, isPromotional),
    })

export const airParams = (airFare: RequestAirFare, gateway: string | undefined = undefined): AirParams => {
    if (airFare === "NotAvailable") return AirParams.notAvailable
    if (airFare === "CruiseOnly") return AirParams.cruiseOnly
    return AirParams.of.flight({
        airClass: airFare,
        gateway: gateway === undefined ? GatewayParam.countryDefault : GatewayParam.of.userSelected({ gateway }),
    })
}

type PriceResponseParams = {
    adults?: number
    fareCode?: FareCode
    vsMember?: boolean
    air?: AirParams
    airFare?: RequestAirFare
    departureGateway?: string
    outboundTo?: string
    returnFrom?: string
    suitePayment?: SuitePayment
    totalAmount?: number
    depositPercentage?: number
    depositDate?: string
    currency?: Currency
    paxPrices?: readonly PaxPrice[]
    inclusionCodes?: readonly InclusionCode[]
    availability?: AvailabilityType
}
export const priceResponse = (
    cruiseCode: string,
    suiteCategory: string,
    {
        adults = 2,
        fareCode = "DoorToDoorV2",
        vsMember = false,
        air,
        airFare = "Economy",
        departureGateway = "ABQ",
        outboundTo = "FLL",
        returnFrom = "LIS",
        suitePayment,
        depositPercentage = 25,
        totalAmount = 11600,
        currency = "USD",
        depositDate = "2022-11-16",
        paxPrices,
        inclusionCodes = [
            "PreDoor2Door",
            "EconomyFlightsWithUpgrade",
            "TransferInternationalAirportToPier",
            "FreeShorex",
            "TransferPierToInternationalAirport",
            "PostDoor2Door",
            "Butler",
            "FoodAndBeverage",
        ],
        availability = "standard",
    }: PriceResponseParams = {},
): PriceResponseV2 => {
    const realSuitePayment = suitePayment ?? deposit(depositPercentage, totalAmount, currency, depositDate)
    const realAir = air ?? airParams(airFare, departureGateway)
    return {
        cruiseCode: CruiseCode.buildUnsafe(cruiseCode),
        suiteCategory,
        occupancy: Occupancy.of(adults),
        fareCode,
        vsMember,
        air: realAir,
        suitePayment: realSuitePayment,
        price: {
            total: realSuitePayment.totalAmount,
            originalTotal: O.none,
            paxPrices:
                paxPrices ??
                F.pipe(
                    RONEA.range(1, adults),
                    RONEA.map((number): PaxPrice => {
                        const total = Amount.divideBy(adults)(realSuitePayment.totalAmount)
                        return {
                            number,
                            total,
                            details: {
                                cruise: cruiseDetail(total.value, total.currency),
                                promotions: [],
                                flights: F.pipe(
                                    realAir,
                                    AirParams.match(
                                        {
                                            flight: (x) =>
                                                flightsDetail(
                                                    0,
                                                    total.currency,
                                                    departureGateway,
                                                    outboundTo,
                                                    returnFrom,
                                                    x.airClass,
                                                    true,
                                                ),
                                        },
                                        () => O.none,
                                    ),
                                ),
                                others: [],
                            },
                        }
                    }),
                ),
            inclusions: Inclusion.listOf(...inclusionCodes),
        },
        availability,
    }
}

type PriceRequestParams = {
    adults?: number
    fareCode?: FareCode
    vsMember?: boolean
    air?: AirParams
    airFare?: RequestAirFare
    departureGateway?: string
    availability?: AvailabilityType
}
export const priceRequest = (
    cruiseCode: string,
    suiteCategory: string,
    {
        adults = 2,
        fareCode = "DoorToDoor",
        vsMember = false,
        availability = "standard",
        air,
        airFare = "Economy",
        departureGateway = "ABQ",
    }: PriceRequestParams,
): PriceRequestV2 => ({
    cruiseCode: CruiseCode.buildUnsafe(cruiseCode),
    occupancy: Occupancy.of(adults),
    fareCode,
    vsMember,
    vsCode: O.none,
    availabilities: [availability],
    air: air ?? airParams(airFare, departureGateway),
    suiteCategory,
})

export const noAvailabilityForOccupancy = (
    cruiseCode: string,
    suiteCategory: string,
    params: PriceRequestParams = {},
): PriceErrorResponseV2 => ({
    request: priceRequest(cruiseCode, suiteCategory, params),
    errorCode: "noAvailabilityForOccupancy",
})
